import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Calendar, Coffee, X, Palmtree, AlertCircle } from 'lucide-react';

const DAYS = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
];

const defaultDay = () => ({ enabled: true, slots: [{ from: '09:00', to: '13:00' }, { from: '17:00', to: '20:00' }], breaks: [] });

export default function Step5Availability({ data = {}, onSave }) {
  const initWeekly = () => {
    const w = {};
    DAYS.forEach(d => { w[d.key] = data?.weekly?.[d.key] || (d.key === 'sunday' ? { enabled: false, slots: [], breaks: [] } : defaultDay()); });
    return w;
  };
  const [weekly, setWeekly] = useState(initWeekly());
  const [leaves, setLeaves] = useState(data?.leaves || []);
  const [holidays, setHolidays] = useState(data?.holidays || []);
  const [overrides, setOverrides] = useState(data?.overrides || []);
  const [maxAdvanceDays, setMaxAdvanceDays] = useState(data?.max_advance_days || 30);

  const [newLeave, setNewLeave] = useState({ from_date: '', to_date: '', reason: '', type: 'full' });
  const [newHoliday, setNewHoliday] = useState({ date: '', label: '' });
  const [newOverride, setNewOverride] = useState({ date: '', enabled: true, from: '', to: '' });

  useEffect(() => { if (data) { setWeekly(initWeekly()); setLeaves(data?.leaves || []); setHolidays(data?.holidays || []); setOverrides(data?.overrides || []); setMaxAdvanceDays(data?.max_advance_days || 30); } /* eslint-disable-next-line */ }, [data]);

  const toggleDay = (dayKey) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], enabled: !p[dayKey].enabled } }));

  const addSlot = (dayKey) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], slots: [...(p[dayKey].slots || []), { from: '09:00', to: '13:00' }] } }));
  const updateSlot = (dayKey, i, field, value) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], slots: p[dayKey].slots.map((s, idx) => idx === i ? { ...s, [field]: value } : s) } }));
  const removeSlot = (dayKey, i) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], slots: p[dayKey].slots.filter((_, idx) => idx !== i) } }));

  const addBreak = (dayKey) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], breaks: [...(p[dayKey].breaks || []), { label: 'Lunch', from: '13:00', to: '14:00' }] } }));
  const updateBreak = (dayKey, i, field, value) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], breaks: p[dayKey].breaks.map((b, idx) => idx === i ? { ...b, [field]: value } : b) } }));
  const removeBreak = (dayKey, i) => setWeekly(p => ({ ...p, [dayKey]: { ...p[dayKey], breaks: p[dayKey].breaks.filter((_, idx) => idx !== i) } }));

  const applyToWeekdays = () => {
    const monday = weekly.monday;
    setWeekly(p => ({
      ...p,
      tuesday: { ...monday }, wednesday: { ...monday }, thursday: { ...monday }, friday: { ...monday },
    }));
  };

  const addLeave = () => {
    if (!newLeave.from_date || !newLeave.to_date) return;
    setLeaves([...leaves, newLeave]);
    setNewLeave({ from_date: '', to_date: '', reason: '', type: 'full' });
  };
  const removeLeave = (i) => setLeaves(leaves.filter((_, idx) => idx !== i));

  const addHoliday = () => {
    if (!newHoliday.date) return;
    setHolidays([...holidays, newHoliday]);
    setNewHoliday({ date: '', label: '' });
  };
  const removeHoliday = (i) => setHolidays(holidays.filter((_, idx) => idx !== i));

  const addOverride = () => {
    if (!newOverride.date) return;
    setOverrides([...overrides, newOverride]);
    setNewOverride({ date: '', enabled: true, from: '', to: '' });
  };
  const removeOverride = (i) => setOverrides(overrides.filter((_, idx) => idx !== i));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ weekly, leaves, holidays, overrides, max_advance_days: maxAdvanceDays });
  };

  return (
    <form id="step-form-5" onSubmit={handleSubmit} className="p-6 lg:p-8 space-y-8">
      {/* Booking window setting */}
      <section className="p-4 rounded-xl border border-slate-200 bg-slate-50/60">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-slate-900">How far ahead can patients book?</h3>
            <p className="text-xs text-slate-500 mt-0.5">Longer windows fill more slots; shorter windows give you flexibility to change your schedule.</p>
          </div>
          <Select value={String(maxAdvanceDays)} onValueChange={(v) => setMaxAdvanceDays(parseInt(v, 10))}>
            <SelectTrigger className="w-40" data-testid="max-advance-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days (1 week)</SelectItem>
              <SelectItem value="14">14 days (2 weeks)</SelectItem>
              <SelectItem value="30">30 days (1 month)</SelectItem>
              <SelectItem value="60">60 days (2 months)</SelectItem>
              <SelectItem value="90">90 days (3 months)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Weekly Schedule */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#3B1C73]" /> Weekly Schedule</h3>
            <p className="text-sm text-slate-500 mt-1">Set your working hours for each day</p>
          </div>
          <button type="button" onClick={applyToWeekdays} className="text-xs text-[#3B1C73] hover:underline">Copy Monday to weekdays</button>
        </div>
        <div className="space-y-3">
          {DAYS.map(day => (
            <div key={day.key} className={`p-4 rounded-xl border ${weekly[day.key].enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={weekly[day.key].enabled} onChange={() => toggleDay(day.key)} className="w-5 h-5 accent-[#3B1C73]" data-testid={`day-toggle-${day.key}`} />
                  <span className="font-semibold text-slate-900 w-24">{day.label}</span>
                  {!weekly[day.key].enabled && <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Closed</span>}
                </label>
                {weekly[day.key].enabled && (
                  <div className="flex gap-2">
                    <button type="button" onClick={() => addSlot(day.key)} className="text-xs text-[#3B1C73] hover:bg-purple-50 px-2 py-1 rounded flex items-center gap-1"><Plus className="w-3 h-3" />Slot</button>
                    <button type="button" onClick={() => addBreak(day.key)} className="text-xs text-amber-600 hover:bg-amber-50 px-2 py-1 rounded flex items-center gap-1"><Coffee className="w-3 h-3" />Break</button>
                  </div>
                )}
              </div>

              {weekly[day.key].enabled && (
                <div className="space-y-2 pl-8">
                  {/* Slots */}
                  {(weekly[day.key].slots || []).map((slot, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs w-14 text-slate-500 font-medium">Slot {i + 1}</span>
                      <Input type="time" value={slot.from} onChange={(e) => updateSlot(day.key, i, 'from', e.target.value)} className="w-32" data-testid={`slot-from-${day.key}-${i}`} />
                      <span className="text-slate-400">to</span>
                      <Input type="time" value={slot.to} onChange={(e) => updateSlot(day.key, i, 'to', e.target.value)} className="w-32" />
                      <button type="button" onClick={() => removeSlot(day.key, i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {/* Breaks */}
                  {(weekly[day.key].breaks || []).map((brk, i) => (
                    <div key={`b-${i}`} className="flex items-center gap-2 p-2 bg-amber-50/60 rounded-lg">
                      <Coffee className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <Input value={brk.label} onChange={(e) => updateBreak(day.key, i, 'label', e.target.value)} placeholder="Lunch" className="w-24" />
                      <Input type="time" value={brk.from} onChange={(e) => updateBreak(day.key, i, 'from', e.target.value)} className="w-32" />
                      <span className="text-slate-400 text-sm">to</span>
                      <Input type="time" value={brk.to} onChange={(e) => updateBreak(day.key, i, 'to', e.target.value)} className="w-32" />
                      <button type="button" onClick={() => removeBreak(day.key, i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(weekly[day.key].slots || []).length === 0 && (
                    <p className="text-xs text-slate-400 italic">No slots — add one above</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Leaves */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><Palmtree className="w-5 h-5 text-[#3B1C73]" /> Leaves</h3>
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
          <div className="grid sm:grid-cols-4 gap-2">
            <div><Label className="text-xs">From</Label><Input type="date" value={newLeave.from_date} onChange={(e) => setNewLeave({ ...newLeave, from_date: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={newLeave.to_date} onChange={(e) => setNewLeave({ ...newLeave, to_date: e.target.value })} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={newLeave.type} onValueChange={(v) => setNewLeave({ ...newLeave, type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Day</SelectItem>
                  <SelectItem value="half_morning">Half Day (Morning)</SelectItem>
                  <SelectItem value="half_evening">Half Day (Evening)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Reason</Label><Input value={newLeave.reason} onChange={(e) => setNewLeave({ ...newLeave, reason: e.target.value })} placeholder="Family event" className="mt-1" /></div>
          </div>
          <button type="button" onClick={addLeave} className="text-sm text-[#3B1C73] hover:bg-purple-50 px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus className="w-4 h-4" /> Add Leave</button>
          {leaves.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              {leaves.map((l, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span>{l.from_date} → {l.to_date} · <span className="text-slate-500">{l.type.replace(/_/g, ' ')}</span>{l.reason && ` · ${l.reason}`}</span>
                  <button type="button" onClick={() => removeLeave(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Holidays */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><Calendar className="w-5 h-5 text-[#3B1C73]" /> Public Holidays</h3>
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
          <div className="grid sm:grid-cols-3 gap-2">
            <div><Label className="text-xs">Date</Label><Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Holiday Name</Label><Input value={newHoliday.label} onChange={(e) => setNewHoliday({ ...newHoliday, label: e.target.value })} placeholder="Diwali, Independence Day..." className="mt-1" /></div>
          </div>
          <button type="button" onClick={addHoliday} className="text-sm text-[#3B1C73] hover:bg-purple-50 px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus className="w-4 h-4" /> Add Holiday</button>
          {holidays.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              {holidays.map((h, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span>{h.date}{h.label && ` — ${h.label}`}</span>
                  <button type="button" onClick={() => removeHoliday(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Date Overrides */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2"><AlertCircle className="w-5 h-5 text-[#3B1C73]" /> Date Overrides</h3>
        <p className="text-xs text-slate-500 mb-3">Override specific dates — e.g., available only 11 AM - 3 PM on Aug 18 instead of normal hours.</p>
        <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
          <div className="grid sm:grid-cols-4 gap-2 items-end">
            <div><Label className="text-xs">Date</Label><Input type="date" value={newOverride.date} onChange={(e) => setNewOverride({ ...newOverride, date: e.target.value })} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={newOverride.enabled ? 'available' : 'closed'} onValueChange={(v) => setNewOverride({ ...newOverride, enabled: v === 'available' })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newOverride.enabled && (
              <>
                <div><Label className="text-xs">From</Label><Input type="time" value={newOverride.from} onChange={(e) => setNewOverride({ ...newOverride, from: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">To</Label><Input type="time" value={newOverride.to} onChange={(e) => setNewOverride({ ...newOverride, to: e.target.value })} className="mt-1" /></div>
              </>
            )}
          </div>
          <button type="button" onClick={addOverride} className="text-sm text-[#3B1C73] hover:bg-purple-50 px-3 py-1.5 rounded-lg flex items-center gap-1"><Plus className="w-4 h-4" /> Add Override</button>
          {overrides.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              {overrides.map((o, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                  <span>{o.date} — {o.enabled ? `Available ${o.from} to ${o.to}` : <span className="text-red-500">Closed</span>}</span>
                  <button type="button" onClick={() => removeOverride(i)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </form>
  );
}
